import React, { useMemo, useState } from 'react';

const EMPTY_VALUE = '-';

function formatDateTime(value) {
  if (!value) {
    return EMPTY_VALUE;
  }

  try {
    return new Intl.DateTimeFormat('fr-FR', {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(new Date(value));
  } catch (error) {
    return String(value);
  }
}

function getTimestamp(value) {
  if (!value) {
    return 0;
  }

  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function normalizeString(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function compareValues(left, right) {
  if (typeof left === 'string' && typeof right === 'string') {
    return left.localeCompare(right, 'fr', { sensitivity: 'base' });
  }

  return Number(left || 0) - Number(right || 0);
}

function getQuestionnaireText(member) {
  const questionnaire = member?.progress?.questionnaire;
  return `${questionnaire?.answeredQuestions || 0}/${questionnaire?.totalQuestions || 0} (${questionnaire?.percent || 0}%)`;
}

function getProfilePercent(member) {
  const personal = member?.progress?.personal?.percent || 0;
  const household = member?.progress?.household?.percent || 0;
  const statistics = member?.progress?.statistics?.percent || 0;
  return Math.round((personal + household + statistics) / 3);
}

function orderMembersForDisplay(members = []) {
  return [...members].sort((left, right) => {
    const leftOwner = left.role === 'owner' ? 0 : 1;
    const rightOwner = right.role === 'owner' ? 0 : 1;

    if (leftOwner !== rightOwner) {
      return leftOwner - rightOwner;
    }

    return getTimestamp(left.createdAt) - getTimestamp(right.createdAt);
  });
}

function buildFamilyRow(family) {
  const orderedMembers = orderMembersForDisplay(family.members);
  const memberA = orderedMembers[0] || null;
  const memberB = orderedMembers[1] || null;

  return {
    uuid: family.uuid,
    familyName: family.name || 'Foyer sans nom',
    inviteCode: family.inviteCode || EMPTY_VALUE,
    familyCreatedAt: family.createdAt || null,
    familyLastAnsweredAt: family.lastAnsweredAt || null,
    membersCount: family.membersCount || orderedMembers.length,
    memberA,
    memberB
  };
}

function getMemberLabel(member) {
  if (!member) {
    return EMPTY_VALUE;
  }
  return member.name || 'Membre sans nom';
}

function buildColumns() {
  return [
    {
      key: 'familyName',
      label: 'Famille',
      placeholder: 'Rechercher une famille',
      getDisplayValue: (row) => row.familyName,
      getSortValue: (row) => row.familyName,
      className: 'min-w-[200px]'
    },
    {
      key: 'familyCreatedAt',
      label: 'Creation famille',
      placeholder: 'Date creation',
      getDisplayValue: (row) => formatDateTime(row.familyCreatedAt),
      getSortValue: (row) => getTimestamp(row.familyCreatedAt),
      className: 'min-w-[200px]'
    },
    {
      key: 'membersCount',
      label: 'Membres',
      placeholder: 'Nombre membres',
      getDisplayValue: (row) => String(row.membersCount),
      getSortValue: (row) => row.membersCount,
      className: 'min-w-[110px]'
    },
    {
      key: 'memberAName',
      label: 'Nom membre A',
      placeholder: 'Rechercher membre A',
      getDisplayValue: (row) => getMemberLabel(row.memberA),
      getSortValue: (row) => getMemberLabel(row.memberA),
      className: 'min-w-[200px]'
    },
    {
      key: 'memberBName',
      label: 'Nom membre B',
      placeholder: 'Rechercher membre B',
      getDisplayValue: (row) => getMemberLabel(row.memberB),
      getSortValue: (row) => getMemberLabel(row.memberB),
      className: 'min-w-[200px]'
    },
    {
      key: 'memberAQuestionnaire',
      label: 'Quiz A',
      placeholder: 'Ex: 10/50',
      getDisplayValue: (row) => getQuestionnaireText(row.memberA),
      getSortValue: (row) => row.memberA?.progress?.questionnaire?.percent || 0,
      className: 'min-w-[160px]'
    },
    {
      key: 'memberBQuestionnaire',
      label: 'Quiz B',
      placeholder: 'Ex: 10/50',
      getDisplayValue: (row) => getQuestionnaireText(row.memberB),
      getSortValue: (row) => row.memberB?.progress?.questionnaire?.percent || 0,
      className: 'min-w-[160px]'
    },
    {
      key: 'inviteCode',
      label: 'Code',
      placeholder: 'Filtrer un code',
      getDisplayValue: (row) => row.inviteCode,
      getSortValue: (row) => row.inviteCode,
      className: 'min-w-[130px]'
    },
    {
      key: 'memberACreatedAt',
      label: 'Inscription A',
      placeholder: 'Date inscription A',
      getDisplayValue: (row) => formatDateTime(row.memberA?.createdAt),
      getSortValue: (row) => getTimestamp(row.memberA?.createdAt),
      className: 'min-w-[190px]'
    },
    {
      key: 'memberBCreatedAt',
      label: 'Inscription B',
      placeholder: 'Date inscription B',
      getDisplayValue: (row) => formatDateTime(row.memberB?.createdAt),
      getSortValue: (row) => getTimestamp(row.memberB?.createdAt),
      className: 'min-w-[190px]'
    },
    {
      key: 'memberAProfileCompletion',
      label: 'Profil A',
      placeholder: 'Ex: 80',
      getDisplayValue: (row) => `${getProfilePercent(row.memberA)}%`,
      getSortValue: (row) => getProfilePercent(row.memberA),
      className: 'min-w-[120px]'
    },
    {
      key: 'memberBProfileCompletion',
      label: 'Profil B',
      placeholder: 'Ex: 80',
      getDisplayValue: (row) => `${getProfilePercent(row.memberB)}%`,
      getSortValue: (row) => getProfilePercent(row.memberB),
      className: 'min-w-[120px]'
    },
    {
      key: 'memberALastAnsweredAt',
      label: 'Derniere reponse A',
      placeholder: 'Date derniere reponse A',
      getDisplayValue: (row) => formatDateTime(row.memberA?.lastAnsweredAt),
      getSortValue: (row) => getTimestamp(row.memberA?.lastAnsweredAt),
      className: 'min-w-[200px]'
    },
    {
      key: 'memberBLastAnsweredAt',
      label: 'Derniere reponse B',
      placeholder: 'Date derniere reponse B',
      getDisplayValue: (row) => formatDateTime(row.memberB?.lastAnsweredAt),
      getSortValue: (row) => getTimestamp(row.memberB?.lastAnsweredAt),
      className: 'min-w-[200px]'
    },
    {
      key: 'familyLastAnsweredAt',
      label: 'Derniere activite foyer',
      placeholder: 'Date activite foyer',
      getDisplayValue: (row) => formatDateTime(row.familyLastAnsweredAt),
      getSortValue: (row) => getTimestamp(row.familyLastAnsweredAt),
      className: 'min-w-[220px]'
    }
  ];
}

function SortableHeader({ column, sortKey, direction, onSort }) {
  const isActive = sortKey === column.key;
  const indicator = isActive ? (direction === 'asc' ? '^' : 'v') : '';

  return (
    <th className={`px-3 py-3 text-left text-xs font-semibold uppercase tracking-[0.12em] text-ptcm-muted dark:text-gray-400 ${column.className || ''}`}>
      <button
        type="button"
        onClick={() => onSort(column.key)}
        className="inline-flex items-center gap-2 transition-colors hover:text-ptcm-ink dark:hover:text-gray-200"
      >
        <span>{column.label}</span>
        <span className="w-3 text-center">{indicator}</span>
      </button>
    </th>
  );
}

function ColumnFilterCell({ column, value, onChange }) {
  return (
    <th className={`px-3 pb-3 ${column.className || ''}`}>
      <input
        type="text"
        value={value}
        onChange={(event) => onChange(column.key, event.target.value)}
        placeholder={column.placeholder}
        className="w-full rounded-xl border border-ptcm-border/80 bg-white/90 px-3 py-2 text-xs text-ptcm-ink outline-none transition-colors placeholder:text-ptcm-muted focus:border-ptcm-accent dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100 dark:placeholder:text-gray-500"
      />
    </th>
  );
}

export default function FamiliesUsageTable({ families = [] }) {
  const [sortKey, setSortKey] = useState('familyCreatedAt');
  const [direction, setDirection] = useState('desc');
  const columns = useMemo(() => buildColumns(), []);
  const [filters, setFilters] = useState(() =>
    columns.reduce((result, column) => ({ ...result, [column.key]: '' }), {})
  );

  const rows = useMemo(() => families.map(buildFamilyRow), [families]);

  const visibleRows = useMemo(() => {
    const filteredRows = rows.filter((row) => {
      return columns.every((column) => {
        const filterValue = normalizeString(filters[column.key]);
        if (!filterValue) {
          return true;
        }

        return normalizeString(column.getDisplayValue(row)).includes(filterValue);
      });
    });

    const sortedRows = [...filteredRows].sort((left, right) => {
      const column = columns.find((candidate) => candidate.key === sortKey);
      if (!column) {
        return 0;
      }

      const comparison = compareValues(column.getSortValue(left), column.getSortValue(right));
      if (comparison !== 0) {
        return direction === 'asc' ? comparison : -comparison;
      }

      return compareValues(left.familyName, right.familyName);
    });

    return sortedRows;
  }, [columns, direction, filters, rows, sortKey]);

  const handleSort = (nextSortKey) => {
    if (sortKey === nextSortKey) {
      setDirection((currentDirection) => (currentDirection === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortKey(nextSortKey);
    setDirection(nextSortKey === 'familyName' ? 'asc' : 'desc');
  };

  const handleFilterChange = (columnKey, nextValue) => {
    setFilters((current) => ({
      ...current,
      [columnKey]: nextValue
    }));
  };

  if (!rows.length) {
    return (
      <div className="rounded-[28px] border border-dashed border-ptcm-border px-6 py-10 text-center text-sm text-ptcm-muted dark:border-gray-800 dark:text-gray-400">
        Aucun foyer enregistre pour le moment.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-ptcm-muted dark:text-gray-400">
        Une ligne par famille, avec tri + filtre/recherche sur chaque colonne.
      </p>

      <div className="overflow-x-auto rounded-[28px] border border-ptcm-border/80 dark:border-gray-800">
        <table className="min-w-full border-collapse">
          <thead className="bg-ptcm-panel/60 dark:bg-gray-950/40">
            <tr>
              {columns.map((column) => (
                <SortableHeader
                  key={column.key}
                  column={column}
                  sortKey={sortKey}
                  direction={direction}
                  onSort={handleSort}
                />
              ))}
            </tr>

            <tr>
              {columns.map((column) => (
                <ColumnFilterCell
                  key={column.key}
                  column={column}
                  value={filters[column.key]}
                  onChange={handleFilterChange}
                />
              ))}
            </tr>
          </thead>

          <tbody>
            {visibleRows.map((row) => (
              <tr key={row.uuid} className="border-t border-ptcm-border/70 dark:border-gray-800">
                {columns.map((column) => (
                  <td key={column.key} className={`px-3 py-3 text-sm text-ptcm-ink dark:text-gray-100 ${column.className || ''}`}>
                    <div className="min-h-[24px] flex items-center">
                      {column.getDisplayValue(row)}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {visibleRows.length !== rows.length ? (
        <p className="text-xs text-ptcm-muted dark:text-gray-400">
          {visibleRows.length} familles affichees sur {rows.length}.
        </p>
      ) : null}
    </div>
  );
}
